import { Button, Grid2 } from '@mui/material'
import { useStudentCourseNameList } from '../../assets/api/useAdminApi'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import { IdAutoCompleteField } from '../../shared/components/formfields/IdAutoCompleteField'
import { DateFormField } from '../../shared/components/formfields/DataFormField'
import { AutoCompleteField } from '../../shared/components/formfields/AutoCompleteField'
import { TextFormField } from '../../shared/components/formfields/TextFormField'
import api from '../../assets/nodeApi/axiosInstance';
import { useToaster } from '../../shared/utils/ToasterProvider'
import useAuthStore from '../../store/useAuthStore';
import usePayoutStore from '../../store/usePayoutStore';
import CustomDivider from '../../shared/utils/CustomDivider';
import { Grid_Gap } from '../../shared/constants';
import { useParams } from 'react-router-dom';
const payoutType = [
    {
        id: 1,
        label: "Fixed",
        value: "Fixed",
    },
    {
        id: 2,
        label: "Variable",
        value: "Variable",
    },
    {
        id: 3,
        label: "Fixed+Variable",
        value: "Fixed_Variable"
    }
]
const AddPayoutForm = ({ hadlePayoutClose, ApartmentOption, isLoadingApart, inputWidth }) => {
    const { payoutDetail, clearPayoutDetail } = usePayoutStore()
    const { id } = useParams()
    const selectedApartment = ApartmentOption?.find(item => item.id?.toString() === (id?.toString() ?? payoutDetail?.apartment_id?.toString()));
    const gridWidth = inputWidth ?? 12
    const queryClient = useQueryClient();
    const { showToaster } = useToaster();
    const { token } = useAuthStore();
    const { data: courseList, isLoading: isCourseLoading } = useStudentCourseNameList();
    const CoursesOptionsForPayout = courseList?.data?.map(course => ({
        id: course?.id,
        label: course?.course_name,
        value: course?.course_name,
    })) || [];
    const mutation = useMutation({
        mutationFn: (formData) => {
            return payoutDetail?.id
                ? api.put(`student-payout/update/${payoutDetail?.id}`, formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                )
                : api.post(`student-payout/create`, formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
        },
        onSuccess: (response) => {
            showToaster('Success', response?.data?.message, 'success');
            queryClient.invalidateQueries({ queryKey: ["unassigned-apartments"] });
            queryClient.invalidateQueries({ queryKey: ["student_course_name_list"] });
            clearPayoutDetail()
        },
        onError: () => {
            showToaster("Error Caught While submiting the form", 'Error', 'error');
        },

    });
    const handlePayoutSubmit = (values, { setSubmitting, resetForm }) => {
        const obj = {
            apartment_id: selectedApartment?.id,
            ...values
        }
        mutation.mutate(obj, {
            onSettled: () => {
                setSubmitting(false);
            },
            onSuccess: () => {
                resetForm();
            }
        });
        hadlePayoutClose()
    }
    return (
        <Formik
            enableReinitialize={true}
            initialValues={{
                course_id: payoutDetail?.course_id || "",
                apartment_id: selectedApartment?.id || payoutDetail?.apartment_id || "",
                // start_date: payoutDetail?.payout_start_date || "",
                from_date: payoutDetail?.from_date || "",
                to_date: payoutDetail?.to_date || "",
                payout_type: payoutDetail?.payout_type || "",
                amount: payoutDetail?.fixed_amount || 0,
                percentage: payoutDetail?.percentage || 0,
            }}
            validationSchema={Yup.object().shape({
                course_id: Yup.string().required("Please Select Course "),
                apartment_id: Yup.string().required("Please Select Apartment"),
                from_date: Yup.string().required("From Date is required"),
                to_date: Yup.string().required("To Date is required"),
                payout_type: Yup.string().required("Select a payout type"),
                amount: Yup.number()
                    .when("payout_type", {
                        is: "Fixed",
                        then: schema =>
                            schema
                                .required("Amount is required for Fixed type")
                                .moreThan(0, "Amount must be greater than 0"),
                    })
                    .when("payout_type", {
                        is: "Fixed_Variable",
                        then: schema =>
                            schema
                                .required("Amount is required")
                                .moreThan(0, "Amount must be greater than 0"),
                    }),

                percentage: Yup.number()
                    .max(100, "Percentage must be at most 100")
                    .when("payout_type", {
                        is: "Variable",
                        then: schema =>
                            schema
                                .required("Percentage is required for Variable type")
                                .moreThan(0, "Percentage must be greater than 0"),
                    })
                    .when("payout_type", {
                        is: "Fixed_Variable",
                        then: schema =>
                            schema
                                .required("Percentage is required")
                                .moreThan(0, "Percentage must be greater than 0"),
                    }),


            })}
            onSubmit={handlePayoutSubmit}

        >
            {({ setFieldValue, values, resetForm }) => (
                <Form>
                    <Grid2 container spacing={"30px"}>
                        <Grid2 children size={gridWidth}>
                            <Field
                                name="course_id"
                                component={IdAutoCompleteField}
                                options={CoursesOptionsForPayout}
                                label="Courses"
                                isRequired={true}
                                isLoading={isCourseLoading}

                            />
                        </Grid2>
                        <Grid2 children size={gridWidth}>
                            <Field
                                name="apartment_id"
                                component={IdAutoCompleteField}
                                options={ApartmentOption}
                                label="Apartments"
                                isRequired={true}
                                isLoading={isLoadingApart}
                                disabled={id}
                            />
                        </Grid2>
                        <Grid2 children size={gridWidth}>
                            <Field
                                name="from_date"
                                component={DateFormField}
                                label="From Date"
                                isRequired={true}
                                maxDate={values.to_date || undefined}
                            />
                        </Grid2>
                        <Grid2 children size={gridWidth}>
                            <Field
                                name="to_date"
                                component={DateFormField}
                                label="To Date"
                                isRequired={true}
                                minDate={values.from_date || undefined}
                            />
                        </Grid2>
                        <Grid2 children size={gridWidth}>
                            <Field
                                name="payout_type"
                                component={AutoCompleteField}
                                options={payoutType}
                                label="Select Payout Type"
                                isRequired={true}
                                onValueChange={() => {
                                    setFieldValue("amount", 0)
                                    setFieldValue("percentage", 0)
                                }}
                            />
                        </Grid2>
                        {
                            values?.payout_type === payoutType[0]?.value && <Grid2 children size={gridWidth}>
                                <Field
                                    name="amount"
                                    component={TextFormField}
                                    label="Amount"
                                    isRequired={true}
                                    type="number"
                                />
                            </Grid2>
                        }

                        {
                            values?.payout_type === payoutType[1]?.value &&
                            <Grid2 children size={gridWidth}>
                                <Field
                                    name="percentage"
                                    component={TextFormField}
                                    label="%"
                                    isRequired={true}
                                    type="number"

                                />
                            </Grid2>
                        }

                        {
                            values?.payout_type === payoutType[2]?.value && <>

                                <Grid2 children size={inputWidth ?? 6}>
                                    <Field
                                        name="amount"
                                        component={TextFormField}
                                        label="Amount"
                                        isRequired={true}
                                        type="number"


                                    />
                                </Grid2>
                                <Grid2 children size={inputWidth ?? 6}>
                                    <Field
                                        name="percentage"
                                        component={TextFormField}
                                        label="%"
                                        isRequired={true}
                                        type="number"
                                    />
                                </Grid2>
                            </>
                        }


                    </Grid2>
                    <CustomDivider />
                    <Grid2 container spacing={Grid_Gap}>
                        <Grid2 children size={gridWidth} >
                            <Button variant='outlined' fullWidth onClick={() => { clearPayoutDetail(); resetForm() }}>Cancel</Button>
                        </Grid2>
                        <Grid2 children size={gridWidth}>
                            <Button variant='contained' fullWidth type="submit"> {Object.keys(payoutDetail).length === 0 ? "Submit" : "Update"}</Button>
                        </Grid2>
                    </Grid2>



                </Form>
            )}
        </Formik>
    )
}

export default AddPayoutForm